import { useAtom } from 'jotai';
import React, { useEffect } from 'react';
import { signingStateAtomFamily } from '../../../../store/evmTransactionStore';
import { CardFooter } from '../../../ui/card';
import LoadingMessage from '../../loading/LoadingMessage';

interface SigningCardFooterProps {
  messageId: string;
  isLoading: boolean;
  isSuccess: boolean;
  errorMsg: string;
  textColor?: string;
  onSign: () => Promise<void>;
  onDecline: () => Promise<void>;
  DeclineButton: React.ComponentType<any>;
  ApproveButton: React.ComponentType<any>;
  isDeclining?: boolean;
}

export const SigningCardFooter = ({
  messageId,
  isLoading,
  isSuccess,
  errorMsg,
  textColor = '#ffffff',
  onSign,
  onDecline,
  DeclineButton,
  ApproveButton,
  isDeclining = false,
}: SigningCardFooterProps) => {
  const [isSigning, setIsSigning] = useAtom(signingStateAtomFamily(messageId));

  const handleSignClick = async () => {
    // Prevent multiple clicks for the same message
    if (isSigning || isLoading) {
      return;
    }

    // Set signing state immediately for this messageId
    setIsSigning(true);
    try {
      await onSign();
    } catch (error) {
      console.error('Error during signing:', error);
      // Reset signing state on any error (user cancellation, etc.)
      setIsSigning(false);
    }
  };

  // Clear signing state when transaction definitively completes or fails
  useEffect(() => {
    if (isSigning && (isSuccess || errorMsg)) {
      setIsSigning(false);
    }
  }, [isSigning, isSuccess, errorMsg, setIsSigning]);

  // Show signing message instead of buttons when signing is active
  if (isSigning || isLoading) {
    return (
      <CardFooter className="bitte-flex bitte-gap-3 bitte-p-4">
        <div className="bitte-flex-1 bitte-text-center bitte-py-3 bitte-pointer-events-none bitte-select-none">
          <div className="bitte-flex bitte-items-center bitte-justify-center bitte-gap-2 bitte-flex-col">
            <LoadingMessage color={textColor} />
            <p className="bitte-text-sm">
              {isSigning && !isLoading
                ? 'Message about to be signed'
                : 'Signature request in progress'}
            </p>
          </div>
        </div>
      </CardFooter>
    );
  }

  // Show normal buttons when not signing
  return (
    <CardFooter className="bitte-flex bitte-gap-3 bitte-p-4">
      <DeclineButton
        onClick={onDecline}
        isLoading={isDeclining}
        disabled={isLoading || isSigning}
        label="Decline"
      />
      <ApproveButton
        onClick={handleSignClick}
        isLoading={isLoading}
        disabled={isDeclining || isSigning}
        label="Sign"
      />
    </CardFooter>
  );
};
